import * as XLSX from 'xlsx';
import { pdf } from '@react-pdf/renderer';
import type { ReportExportContext } from './context';
import { buildReportWorkbook } from './buildWorkbook';
import { downloadBlob, safeFilenameDate } from './download';
import { gatherCustomerDetails, gatherInventoryTracking } from './gather';
import type { InventoryItemType } from '../../../services/report.service';
import { ReportPdfDocument } from '../components/ReportPdfDocument';
import { captureSvgAsPng } from './chartImage';

export const inventoryItemTypeFor = (mode: ReportExportContext['mode']): InventoryItemType | null =>
  mode === 'greenBeans' ? 'green_bean'
    : mode === 'roastedBeans' ? 'roasted_bulk'
      : mode === 'finishedGoods' ? 'finished_good'
        : null;

export const inventoryRowsFor = (ctx: ReportExportContext): ReadonlyArray<{ itemId: string }> =>
  ctx.mode === 'greenBeans' ? ctx.greenBeansReport.rows
    : ctx.mode === 'roastedBeans' ? ctx.roastedBeansReport.rows
      : ctx.mode === 'finishedGoods' ? ctx.finishedGoodsReport.rows
        : [];

export async function exportReportExcel(ctx: ReportExportContext): Promise<{ partialFailures: number }> {
  let partialFailures = 0;
  const opts: Parameters<typeof buildReportWorkbook>[1] = {};

  const itemType = inventoryItemTypeFor(ctx.mode);
  if (itemType) {
    const g = await gatherInventoryTracking(inventoryRowsFor(ctx), itemType);
    opts.inventoryTracking = g.tracking;
    partialFailures += g.failed;
  } else if (ctx.mode === 'customer') {
    const g = await gatherCustomerDetails(ctx.customerReport.customers, { startDate: ctx.startDate, endDate: ctx.endDate });
    opts.customerDetails = g.details;
    partialFailures += g.failed;
  }

  const wb = buildReportWorkbook(ctx, opts);
  const arrayBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  downloadBlob(blob, `report-${ctx.mode}-${safeFilenameDate(ctx.startDate)}-${safeFilenameDate(ctx.endDate)}.xlsx`);
  return { partialFailures };
}

export async function exportReportPdf(ctx: ReportExportContext): Promise<{ partialFailures: number; chartSkipped: boolean }> {
  let partialFailures = 0;
  let chartSkipped = false;
  const itemType = inventoryItemTypeFor(ctx.mode);
  let inventoryTracking: Awaited<ReturnType<typeof gatherInventoryTracking>>['tracking'] | undefined;

  if (itemType) {
    const g = await gatherInventoryTracking(inventoryRowsFor(ctx), itemType);
    inventoryTracking = g.tracking;
    partialFailures += g.failed;
  }

  let customerDetails: Awaited<ReturnType<typeof gatherCustomerDetails>>['details'] | undefined;
  if (ctx.mode === 'customer') {
    const g = await gatherCustomerDetails(ctx.customerReport.customers, { startDate: ctx.startDate, endDate: ctx.endDate });
    customerDetails = g.details;
    partialFailures += g.failed;
  }

  let salesChartPng: string | undefined;
  if (ctx.mode === 'business') {
    const svg = document.querySelector('[data-test-id="business-trend-card"] svg') as SVGSVGElement | null;
    if (svg) {
      try {
        salesChartPng = await captureSvgAsPng(svg);
      } catch {
        chartSkipped = true;
      }
    } else if (ctx.businessReport.revenueTrend.length > 0) {
      chartSkipped = true;
    }
  }

  const blob = await pdf(
    <ReportPdfDocument
      mode={ctx.mode}
      currencySymbol={ctx.currencySymbol}
      startDate={ctx.startDate}
      endDate={ctx.endDate}
      roastingReport={ctx.roastingReport}
      greenBeansReport={ctx.greenBeansReport}
      roastedBeansReport={ctx.roastedBeansReport}
      finishedGoodsReport={ctx.finishedGoodsReport}
      businessReport={ctx.businessReport}
      customerReport={ctx.customerReport}
      t={ctx.t}
      inventoryTracking={inventoryTracking}
      inventoryItemType={itemType ?? undefined}
      customerDetails={customerDetails}
      salesChartPng={salesChartPng}
    />
  ).toBlob();
  downloadBlob(
    blob,
    `report-${ctx.mode}-${safeFilenameDate(ctx.startDate)}-${safeFilenameDate(ctx.endDate)}.pdf`,
    { openInNewTab: true },
  );
  return { partialFailures, chartSkipped };
}
